/**
 * «Доставки» → вкладка «Заявки» (`?tab=orders`): заявки на курьера, оформленные из prokat через API
 * Яндекс Доставки и Dostavista (`GET /api/delivery/orders`, таблица `delivery_order`), по дню
 * заказа. Клик по сделке — та же панель справа, что у списков доставок. Спека
 * `docs/specs/2026-10-22-delivery-order-design.md`, раздел «Журнал заявок».
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { DateRangePicker } from "@/components/DateRangePicker";
import type { OrderState } from "@/lib/dealDelivery";
import {
  addDays,
  dm,
  iso,
  type JournalOrder,
  loadDeliveryOrders,
  phone,
} from "@/lib/delivery";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const chip = (on: boolean) =>
  `h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ` +
  (on
    ? "border-accent bg-accent-dim text-fg"
    : "border-border text-fg-muted hover:bg-bg-hover");

const STATE_TONE: Record<OrderState, string> = {
  new: "bg-warn-bg text-warn",
  search: "bg-warn-bg text-warn",
  found: "bg-warn-bg text-warn",
  pickup: "bg-warn-bg text-warn",
  delivered: "bg-ok-bg text-ok",
  cancelled: "bg-neutral-bg text-fg-muted",
  failed: "bg-err-bg text-err",
};
const CONTRACT: Record<JournalOrder["contract_state"], string> = {
  "": "—",
  ok: "строка в договоре",
  off: "запись в договор выключена",
  no_contract: "нет договора",
  failed: "в договор не записалась",
};
const NOTIFIED: Record<string, string> = {
  found: "назначен",
  pickup: "выехал",
};

const rub = (n: number | null) =>
  n == null ? "—" : `${Math.round(n).toLocaleString("ru-RU")} ₽`;
const hm = (t: string | null) => (t ? t.slice(11, 16) : "");
const when = (t: string | null) => (t ? `${dm(t.slice(0, 10))} ${hm(t)}` : "—");

type Provider = "" | "yandex" | "dostavista";

export function DeliveryOrders({
  from,
  to,
  onPeriod,
  onOpen,
}: {
  from: string;
  to: string;
  onPeriod: (from: string, to: string) => void;
  onOpen: (x: JournalOrder) => void;
}) {
  const [provider, setProvider] = useState<Provider>("");
  const [live, setLive] = useState(false);
  const q = useQuery({
    queryKey: ["delivery-orders", from, to],
    queryFn: () => loadDeliveryOrders(from, to),
    refetchInterval: 60_000,
  });
  const r = q.data;
  const rows = (r?.orders ?? []).filter(
    (x) => (!provider || x.provider === provider) && (!live || x.live),
  );
  const t = r?.totals;
  return (
    <div className="flex flex-col gap-3" data-annot="заявки на курьера">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangePicker
          label="заказаны"
          from={from}
          to={to}
          onChange={onPeriod}
        />
        {(
          [
            ["", "Все службы"],
            ["yandex", "Яндекс"],
            ["dostavista", "Dostavista"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={chip(provider === id)}
            onClick={() => setProvider(id)}
          >
            {label}
            {id && t ? ` ${t.providers[id]}` : ""}
          </button>
        ))}
        <button
          type="button"
          className={chip(live)}
          onClick={() => setLive(!live)}
          title="Курьера ищут, назначен или везёт"
        >
          Только в работе
        </button>
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">…</div>}
      {q.isError && (
        <div className="text-[12.5px] text-err">
          Не удалось получить заявки: {q.error.message}
        </div>
      )}
      {t && (
        <div
          className="flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-fg-muted"
          data-annot="итоги заявок"
        >
          <span>
            заявок <b className="tabular-nums text-fg">{t.count}</b>
          </span>
          <span>
            в работе <b className="tabular-nums text-fg">{t.live}</b>
          </span>
          <span>
            доставлено <b className="tabular-nums text-fg">{t.delivered}</b>
          </span>
          <span>
            отменено и не получилось{" "}
            <b className="tabular-nums text-fg">{t.cancelled}</b>
          </span>
          <span title="Сумма по заявкам в работе и доставленным">
            службам <b className="tabular-nums text-fg">{rub(t.price)}</b>
          </span>
          <span title="Цена клиенту в договоре, те же заявки">
            клиентам{" "}
            <b className="tabular-nums text-fg">{rub(t.client_price)}</b>
          </span>
        </div>
      )}
      {r && (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr className="border-b border-border">
                <th className="px-3 py-2 font-normal">Заказ</th>
                <th className="px-3 py-2 font-normal">Сделка</th>
                <th className="px-3 py-2 font-normal">Куда и когда</th>
                <th className="px-3 py-2 font-normal">Служба</th>
                <th className="px-3 py-2 text-right font-normal">
                  Служба / клиенту
                </th>
                <th className="px-3 py-2 font-normal">Шаг и курьер</th>
                <th className="px-3 py-2 font-normal">Договор и клиент</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-fg-muted">
                    За период заявок нет.
                  </td>
                </tr>
              )}
              {rows.map((x) => (
                <OrderRow key={x.id} x={x} onOpen={onOpen} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrderRow({
  x,
  onOpen,
}: {
  x: JournalOrder;
  onOpen: (x: JournalOrder) => void;
}) {
  const [a, b] = x.window;
  const сообщено = Object.entries(x.notified)
    .filter(([k]) => NOTIFIED[k])
    .map(([k, v]) => `${NOTIFIED[k]}${v.preview ? " (предпросмотр)" : ""}`);
  return (
    <tr
      className="border-b border-border align-top last:border-0"
      data-annot={`заявка ${x.id}`}
    >
      <td className="whitespace-nowrap px-3 py-2">
        <div className="tabular-nums">{when(x.date)}</div>
        <div className="text-fg-muted">{x.user_name || "—"}</div>
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          className="font-medium tabular-nums text-fg hover:underline"
          onClick={() => onOpen(x)}
        >
          {x.deal_id}
        </button>
        {x.city && <span className="ml-1.5 text-fg-muted">{x.city}</span>}
        <div className="text-fg-muted">{x.client.name || "—"}</div>
        {x.client.phones[0] && (
          <div className="tabular-nums text-fg-faint">
            {phone(x.client.phones[0])}
          </div>
        )}
      </td>
      <td className="max-w-[18rem] px-3 py-2">
        <div className="font-medium">
          {x.direction === "start" ? "↑ доставить" : "↓ забрать"}{" "}
          <span className="tabular-nums">
            {a ? `${dm(a.slice(0, 10))} ${hm(a)}–${hm(b)}` : ""}
          </span>
        </div>
        <div className="text-fg-muted">{x.address || "—"}</div>
      </td>
      <td className="px-3 py-2">
        <div>
          {x.provider_label}
          {x.stand === "test" && (
            <span className="ml-1 rounded bg-warn-bg px-1 text-[11px] text-warn">
              тест
            </span>
          )}
        </div>
        <div className="text-fg-muted">{x.variant_label}</div>
        <div
          className="max-w-[9rem] truncate text-[11.5px] text-fg-faint"
          title={x.external_id}
        >
          {x.external_id ? `№${x.external_id}` : "без номера"}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
        <div>{rub(x.price)}</div>
        <div className="text-fg-muted">{rub(x.client_price)}</div>
      </td>
      <td className="px-3 py-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[11.5px] ${STATE_TONE[x.state]}`}
          title={x.status}
        >
          {x.state_label}
        </span>
        {(x.courier_name || x.car) && (
          <div className="mt-1">
            {[x.courier_name, x.car].filter(Boolean).join(", ")}
          </div>
        )}
        {x.courier_phone && (
          <div className="tabular-nums text-fg-muted">{x.courier_phone}</div>
        )}
        {x.eta && x.live && <div className="text-fg-muted">к {hm(x.eta)}</div>}
        {x.tracking_url && (
          <a
            href={x.tracking_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-fg-muted hover:underline"
          >
            слежение <ExternalLink size={11} />
          </a>
        )}
        {x.error && (
          <div className="text-[11.5px] text-fg-faint">{x.error}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <div
          className={
            x.contract_state === "failed" ? "text-err" : "text-fg-muted"
          }
          title={x.contract_detail}
        >
          {CONTRACT[x.contract_state]}
        </div>
        <div className="text-fg-muted">
          клиенту: {сообщено.length ? сообщено.join(", ") : "—"}
        </div>
      </td>
    </tr>
  );
}

/** Период вкладки по умолчанию: 30 дней назад по сегодня. */
export const ordersPeriod = (from: string | null, to: string | null) => {
  const today = iso(new Date());
  const b = to || today;
  return { from: from || addDays(b, -30), to: b };
};
